import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
  clearOverride,
  envList,
  getDefaultEnvName,
  getOverride,
  saveOverride,
} from 'app/env-helpers';
import { FeatureFlagValue, FeatureHash } from 'app/feature-toggle-list';
import { FeatureToggleService } from 'app/shared/services/feature-toggle.service';

//

interface _IFeatureOverride {
  key :string;
  value :boolean;
  override :boolean | null;
}

//

@Component({
  selector: 'lum-overrides',
  templateUrl: './overrides.component.html',
  styleUrls: ['./overrides.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
})
export class OverridesComponent implements OnInit, OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _searchTerm$ = new BehaviorSubject<string>('');

  isOpen :boolean = false;
  toggles :_IFeatureOverride[] = [];
  show = localStorage.getItem('luminousOverridesShow') === 'true';
  tab :number = 0;
  envs :{ key :string; name :string; default ?:boolean }[] = envList;
  envNameDefault :string;
  envNameOverride :string;
  tenantLocalToggles :string;
  urlOverride :string;
  urlV2Override :string;
  searchControl :FormControl<string> = new FormControl<string>('');
  filteredToggles$ :Observable<_IFeatureOverride[]>;
  isSyncToBackendLoading :boolean = false;

  constructor(
    private featureToggleService :FeatureToggleService,
    private cd :ChangeDetectorRef,
  ) {
    const localEnv = this.getLocalEnv();
    this.tenantLocalToggles = localEnv?.tenantLocalToggles || '';
    this.urlOverride = localEnv?.url || '';
    this.urlV2Override = localEnv?.urlV2 || '';
    this.envNameDefault = getDefaultEnvName();
    this.envNameOverride = localEnv?.env || this.envNameDefault;
    this.envs.forEach((env) => {
      env.default = env.key === this.envNameDefault;
    });

    this.#exposeGlobalDevApi();
  }

  ngOnInit() {
    // proxy the search control value to an intermediary BehaviorSubject
    this.searchControl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((searchTerm) => {
        this._searchTerm$.next(searchTerm);
      });

    // build my observables
    const searchTerm$ = this._searchTerm$.asObservable();
    const toggles$ = this.featureToggleService.featureToggles$.pipe(
      takeUntil(this._onDestroy$),
      map((togglesHash :FeatureHash) => this.#buildTogglesFrom(togglesHash)),
    );

    // subscribe to the toggles service
    toggles$.subscribe((toggles) => {
      this.toggles = toggles;
    });

    // generate the filtered goggles observable combining the toggles API
    // response and the user's search input
    this.filteredToggles$ = combineLatest([searchTerm$, toggles$]).pipe(
      takeUntil(this._onDestroy$),
      map(([searchTerm, toggles]) => this.#filterToggles(toggles, searchTerm)),
    );
  }

  ngOnDestroy() {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  onTab(next :number) {
    this.tab = next;
  }

  open() {
    // ensure the toggles are filtered based on the value, if it exists.
    this._searchTerm$.next(this.searchControl.value || '');

    this.isOpen = true;
    this.cd.detectChanges();
  }

  close() {
    this.isOpen = false;
    this.cd.detectChanges();
  }

  onRadioChange(toggle) {
    saveOverride('toggles', {
      ...this.getLocalToggles(),
      [toggle.key]: toggle.override,
    });
  }

  getCalcValue(toggle) {
    return toggle.override ?? toggle.value;
  }

  hasSomeOverride() :boolean {
    const hasToggleOverride = this.toggles.some(
      (toggle) => typeof toggle.override === 'boolean',
    );
    const hasEnvOverride = this.envNameOverride !== this.envNameDefault;

    return (
      hasToggleOverride ||
      hasEnvOverride ||
      !!this.urlOverride ||
      !!this.urlV2Override
    );
  }

  getLocalEnv() :Record<string, any> {
    return getOverride().environment;
  }

  getLocalToggles() :FeatureHash {
    return getOverride().toggles;
  }

  onEnvChange(e) {
    this.saveEnv('env', e.target.value);
  }

  onUrlChange(e) {
    this.saveEnv('url', e.target.value);
  }

  onUrlV2Change(e) {
    this.saveEnv('urlV2', e.target.value);
  }

  applyTenantFeatureFlag() :void {
    this.isSyncToBackendLoading = true;
    this.featureToggleService
      .getTenantFeatureFlags(this.tenantLocalToggles)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((response :any) => {
        if (response.featureFlags) {
          const updatedFeatureFlags = {
            ...this.getLocalToggles(),
            ...response.featureFlags,
          };

          this.saveEnv('tenantLocalToggles', this.tenantLocalToggles);
          saveOverride('toggles', updatedFeatureFlags);

          this.updateTenantFeatureFlags(updatedFeatureFlags);
        }
      });
  }

  updateTenantFeatureFlags(featureFlags :any = []) :void {
    this.featureToggleService
      .updateTenantFeatureFlags({
        featureFlags,
      })
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this.isSyncToBackendLoading = false;
        window.location.reload();
      });
  }

  syncToBackend() :void {
    this.isSyncToBackendLoading = true;
    const localToggles = this.getLocalToggles();

    // THIS CHECKS THE NULL VALUES AND CHANGE TO DEFAULT VALUE
    Object.keys(localToggles).forEach((key :FeatureFlagValue) => {
      if (localToggles[key] === null) {
        localToggles[key] = this.featureToggleService.isActive(key);
      }
    });

    this.updateTenantFeatureFlags(localToggles);
  }

  resetFeatureFlag() :void {
    this.#resetTogglesInMemory();
    const featureToggles :Record<string, boolean> = {};

    this.toggles.forEach((toggle) => {
      featureToggles[toggle.key] = null;
    });

    this.saveEnv('tenantLocalToggles', '');
    saveOverride('toggles', featureToggles);

    this.syncToBackend();
  }

  saveEnv(key, value) {
    saveOverride('environment', {
      ...this.getLocalEnv(),
      [key]: value,
    });
  }

  reset() {
    clearOverride();
    this.#resetEnvInMemory();
    this.#resetTogglesInMemory();
    this.updateTenantFeatureFlags([]);
    this.cd.detectChanges();
  }

  resetEnv() {
    saveOverride('environment', {});
    this.#resetEnvInMemory();
    this.cd.detectChanges();
  }

  resetToggle(toggle :FeatureFlagValue) {
    const toggleOverrides = this.getLocalToggles();

    if (toggleOverrides && Object.hasOwn(toggleOverrides, toggle)) {
      delete toggleOverrides[toggle];
    }

    saveOverride('toggles', toggleOverrides ?? {}); // keep it an object

    this.toggles.some((_toggle :_IFeatureOverride) => {
      if (_toggle.key !== toggle) {
        return false;
      } // `continue;`

      _toggle.override = null;

      return true; // AKA `break;`
    });
    this.cd.detectChanges();
  }

  resetToggles(options ?:{ nullOnly :boolean }) {
    options = {
      nullOnly: false,
      ...options,
    };

    let toggleOverrides = null;

    if (options.nullOnly) {
      toggleOverrides = this.getLocalToggles();

      for (const toggle in toggleOverrides) {
        if (!Object.hasOwn(toggleOverrides, toggle)) {
          continue;
        }

        if (toggleOverrides[toggle] == null) {
          delete toggleOverrides[toggle];
        }
      }
    }

    saveOverride('toggles', toggleOverrides ?? {}); // keep it an object
    this.#resetTogglesInMemory(toggleOverrides);

    this.cd.detectChanges();
  }

  #buildTogglesFrom(togglesHash :FeatureHash) :_IFeatureOverride[] {
    const _localToggles = this.getLocalToggles();

    return Object.entries(togglesHash).map(([key, value]) => ({
      key,
      value,
      override:
        typeof _localToggles?.[key] === 'boolean' ? _localToggles[key] : null,
    }));
  }

  #exposeGlobalDevApi() {
    const _component = this;
    const _feature = this.featureToggleService;

    window._LumOverrideAPI = Object.freeze({
      __proto__: null,
      tidyUpToggles: () => this.resetToggles({ nullOnly: true }),
    });
  }

  #filterToggles(
    toggles :_IFeatureOverride[],
    searchTerm :string,
  ) :_IFeatureOverride[] {
    if (!searchTerm) {
      return toggles;
    }

    return toggles.filter((toggle :_IFeatureOverride) :boolean =>
      toggle.key.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }

  #resetEnvInMemory() {
    this.urlOverride = '';
    this.urlV2Override = '';
    this.envNameOverride = this.envNameDefault;
  }

  #resetTogglesInMemory(overrides ?:FeatureHash) {
    this.toggles = this.toggles.map(
      (toggle :_IFeatureOverride) :_IFeatureOverride => ({
        ...toggle,
        override: overrides ? toggle.key in overrides : null,
      }),
    );
  }
}
